import React, { useState } from "react";
import {
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  Box,
  Button,
  IconButton,
  Paper,
  Chip,
  Divider,
  InputAdornment,
  makeStyles
} from "@material-ui/core";
import { Add, Delete, Security, Assignment, CheckCircle, Warning, Group } from "@material-ui/icons";
import { Alert } from "@material-ui/lab";

const useStyles = makeStyles((theme) => ({
  root: {
    padding: 0,
  },
  sectionHeader: {
    display: 'flex',
    alignItems: 'center',
    marginBottom: theme.spacing(3),
    marginTop: theme.spacing(3),
    color: theme.palette.primary.main,
  },
  sectionIcon: {
    marginRight: theme.spacing(1),
  },
  sectionTitle: {
    fontSize: '1.25rem',
    fontWeight: 600,
  },
  tableContainer: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  table: {
    minWidth: 650,
  },
  addButton: {
    marginBottom: theme.spacing(2),
  },
  emptyState: {
    textAlign: "center",
    padding: theme.spacing(4),
    color: theme.palette.text.secondary,
    backgroundColor: theme.palette.grey[50],
    borderRadius: theme.shape.borderRadius,
    border: `1px dashed ${theme.palette.divider}`,
  },
  mappedChip: {
    backgroundColor: theme.palette.success.main,
    color: "white",
  },
  missingChip: {
    backgroundColor: theme.palette.warning.main,
    color: "white",
  },
  successAlert: {
    marginBottom: theme.spacing(2),
  },
  divider: {
    margin: theme.spacing(4, 0),
  },
  chipTextField: {
    '& .MuiOutlinedInput-root': {
      padding: theme.spacing(0.5),
      minHeight: '40px',
      alignItems: 'flex-start',
    },
    '& .MuiInputBase-input': {
      minWidth: '120px',
      margin: theme.spacing(0.25),
    },
  },
  chipsWrapper: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    margin: theme.spacing(0.25),
  },
  chipInside: {
    height: '24px',
    fontSize: '0.75rem',
  },
  inputContainer: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    minHeight: '40px',
    border: `1px solid ${theme.palette.grey[400]}`,
    borderRadius: theme.shape.borderRadius,
    padding: theme.spacing(0.5),
    '&:hover': {
      borderColor: theme.palette.text.primary,
    },
    '&.focused': {
      borderColor: theme.palette.primary.main,
      borderWidth: '2px',
      padding: theme.spacing(0.5) - 1, // Compensate for thicker border
    },
  },
  inputField: {
    border: 'none',
    outline: 'none',
    fontSize: '0.875rem',
    fontFamily: theme.typography.fontFamily,
    minWidth: '120px',
    flex: 1,
    padding: theme.spacing(0.5),
    backgroundColor: 'transparent',
  }
}));

const standardClaims = [
  {
    claim: "email",
    description: "User's email address",
    required: true,
    placeholder: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/emailaddress"
  },
  {
    claim: "givenName",
    description: "User's first name",
    required: true,
    placeholder: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/givenname"
  },
  {
    claim: "familyName",
    description: "User's last name",
    required: true,
    placeholder: "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/surname"
  },
  {
    claim: "groups",
    description: "User's group memberships",
    required: true,
    placeholder: "http://schemas.microsoft.com/ws/2008/06/identity/claims/groups"
  }
];

const AccessClaimsConfig = ({
  roleMappings,
  claimMappings,
  applications,
  protocol,
  onRoleMappingUpdate,
  onClaimMappingUpdate
}) => {
  const classes = useStyles();
  const [newGroupInputs, setNewGroupInputs] = useState({});
  const [focusedInputs, setFocusedInputs] = useState({});

  const handleAddRoleRow = () => {
    const newRow = {
      application: applications.length > 0 ? applications[0].name : "",
      adGroups: [], // Store as array of groups
      adGroupType: "CLIENT", // Default to client AD groups
      role: "User"
    };
    onRoleMappingUpdate([...roleMappings, newRow]);
  };

  const handleDeleteRoleRow = (index) => {
    const newData = roleMappings.filter((_, i) => i !== index);
    onRoleMappingUpdate(newData);
    // Clean up input state
    const newInputs = { ...newGroupInputs };
    delete newInputs[index];
    setNewGroupInputs(newInputs);
  };

  const handleUpdateRoleRow = (index, field, value) => {
    const newData = [...roleMappings];
    newData[index][field] = value;
    onRoleMappingUpdate(newData);
  };

  const handleAddGroup = (rowIndex) => {
    const groupName = newGroupInputs[rowIndex];
    if (!groupName || !groupName.trim()) return;

    const newData = [...roleMappings];
    const currentGroups = newData[rowIndex].adGroups || [];

    // Check if group already exists
    if (currentGroups.includes(groupName.trim())) return;

    newData[rowIndex].adGroups = [...currentGroups, groupName.trim()];
    onRoleMappingUpdate(newData);

    // Clear input
    setNewGroupInputs(prev => ({
      ...prev,
      [rowIndex]: ''
    }));
  };

  const handleRemoveGroup = (rowIndex, groupIndex) => {
    const newData = [...roleMappings];
    const currentGroups = [...(newData[rowIndex].adGroups || [])];
    currentGroups.splice(groupIndex, 1);
    newData[rowIndex].adGroups = currentGroups;
    onRoleMappingUpdate(newData);
  };

  const handleInputChange = (rowIndex, value) => {
    setNewGroupInputs(prev => ({
      ...prev,
      [rowIndex]: value
    }));
  };

  const handleKeyPress = (event, rowIndex) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleAddGroup(rowIndex);
    }
  };

  const handleFocus = (rowIndex) => {
    setFocusedInputs(prev => ({ ...prev, [rowIndex]: true }));
  };

  const handleBlur = (rowIndex) => {
    setFocusedInputs(prev => ({ ...prev, [rowIndex]: false }));
  };

  const handleClaimUpdate = (claimName, value) => {
    onClaimMappingUpdate({
      ...claimMappings,
      [claimName]: value
    });
  };

  const getMappingStatus = (claimName) => {
    return claimMappings[claimName] && claimMappings[claimName].trim() !== "";
  };

  const allMapped = standardClaims.every(claim => getMappingStatus(claim.claim));

  return (
    <Box className={classes.root}>
      {/* Role Mapping Section */}
      <div className={classes.sectionHeader}>
        <Security className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Role & Access Mapping
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" paragraph>
        Define role mappings between your applications, Active Directory groups, and access levels.
        Users in specific AD groups will automatically receive the corresponding roles in each application.
      </Typography>

      <Box style={{ marginBottom: 16, padding: 12, backgroundColor: '#e3f2fd', borderRadius: 4, border: '1px solid #90caf9' }}>
        <Typography variant="body2" style={{ color: '#1565c0', fontWeight: 500 }}>
          💡 <strong>Tip:</strong> You can add multiple AD groups for each application role.
          Type a group name and press Enter to add it as a chip. Click the × on any chip to remove it.
        </Typography>
      </Box>

      <Box style={{ marginBottom: 16, padding: 12, backgroundColor: '#fff3e0', borderRadius: 4, border: '1px solid #ffcc02' }}>
        <Typography variant="body2" style={{ color: '#e65100', fontWeight: 500 }}>
          🔄 <strong>AD Group Types:</strong>
        </Typography>
        <Typography variant="body2" style={{ color: '#bf360c', marginTop: 4 }}>
          • <strong>Client AD Groups:</strong> Groups from your organization's Active Directory (e.g., "Marketing_Team", "Finance_Users")
        </Typography>
        <Typography variant="body2" style={{ color: '#bf360c' }}>
          • <strong>Accenture AD Groups:</strong> Groups from Accenture's internal Active Directory (e.g., "ACN_Portal_Admins", "ACN_Consultants")
        </Typography>
      </Box>

      <Button
        variant="contained"
        color="primary"
        startIcon={<Add />}
        onClick={handleAddRoleRow}
        className={classes.addButton}
        disabled={applications.length === 0}
        size="small"
      >
        Add Role Mapping
      </Button>

      {applications.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            No applications available
          </Typography>
          <Typography variant="body2" color="textSecondary">
            Please add applications in the previous step to configure role mappings.
          </Typography>
        </Paper>
      ) : roleMappings.length === 0 ? (
        <Paper className={classes.emptyState}>
          <Security style={{ fontSize: 36, marginBottom: 8 }} color="disabled" />
          <Typography variant="subtitle2" color="textSecondary" gutterBottom>
            No role mappings configured
          </Typography>
          <Typography variant="body2" color="textSecondary" gutterBottom>
            Add role mappings to control user access to your applications.
          </Typography>
          <Typography variant="body2" style={{ color: '#666', fontSize: '0.8rem', fontStyle: 'italic' }}>
            Example: Map "Administrators, IT Team, Managers" groups to Admin role for an application
          </Typography>
        </Paper>
      ) : (
        <Paper className={classes.tableContainer}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell><strong>Application</strong></TableCell>
                <TableCell>
                  <Box display="flex" alignItems="center">
                    <strong>Active Directory Groups</strong>
                    <Chip
                      label="Multiple allowed"
                      size="small"
                      variant="outlined"
                      color="primary"
                      style={{ marginLeft: 8, height: 20, fontSize: '0.7rem' }}
                    />
                  </Box>
                </TableCell>
                <TableCell><strong>AD Group Type</strong></TableCell>
                <TableCell><strong>Assigned Role</strong></TableCell>
                <TableCell width={100}><strong>Actions</strong></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {roleMappings.map((row, index) => (
                <TableRow key={index}>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.application}
                        onChange={(e) => handleUpdateRoleRow(index, 'application', e.target.value)}
                        variant="outlined"
                      >
                        {applications.map((app) => (
                          <MenuItem key={app.name} value={app.name}>
                            {app.name}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <Box
                      className={`${classes.inputContainer} ${focusedInputs[index] ? 'focused' : ''}`}
                    >
                      <Group color="action" style={{ marginRight: 8, fontSize: '1rem' }} />

                      {/* Render existing groups as chips inside the container */}
                      {(row.adGroups && row.adGroups.length > 0) && (
                        <>
                          {row.adGroups.map((group, groupIndex) => (
                            <Chip
                              key={groupIndex}
                              label={group}
                              size="small"
                              color="primary"
                              className={classes.chipInside}
                              onDelete={() => handleRemoveGroup(index, groupIndex)}
                              deleteIcon={<Delete style={{ fontSize: '14px' }} />}
                              style={{ margin: 2 }}
                            />
                          ))}
                        </>
                      )}

                      {/* Input field inside the container */}
                      <input
                        className={classes.inputField}
                        placeholder={
                          (row.adGroups && row.adGroups.length > 0)
                            ? "Type another group and press Enter..."
                            : (row.adGroupType === 'ACCENTURE'
                                ? "Type Accenture AD group (e.g., ACN_Portal_Admins) and press Enter..."
                                : "Type Client AD group (e.g., Marketing_Team) and press Enter...")
                        }
                        value={newGroupInputs[index] || ''}
                        onChange={(e) => handleInputChange(index, e.target.value)}
                        onKeyPress={(e) => handleKeyPress(e, index)}
                        onFocus={() => handleFocus(index)}
                        onBlur={() => handleBlur(index)}
                      />
                    </Box>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.adGroupType || 'CLIENT'}
                        onChange={(e) => handleUpdateRoleRow(index, 'adGroupType', e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value="CLIENT">Client AD Groups</MenuItem>
                        <MenuItem value="ACCENTURE">Accenture AD Groups</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <FormControl fullWidth size="small">
                      <Select
                        value={row.role}
                        onChange={(e) => handleUpdateRoleRow(index, 'role', e.target.value)}
                        variant="outlined"
                      >
                        <MenuItem value="Admin">Admin</MenuItem>
                        <MenuItem value="User">User</MenuItem>
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell>
                    <IconButton
                      onClick={() => handleDeleteRoleRow(index)}
                      color="secondary"
                      size="small"
                    >
                      <Delete />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}

      <Divider className={classes.divider} />

      {/* Claim Mapping Section */}
      <div className={classes.sectionHeader}>
        <Assignment className={classes.sectionIcon} />
        <Typography className={classes.sectionTitle}>
          Claim Attribute Mapping
        </Typography>
      </div>

      <Typography variant="body2" color="textSecondary" paragraph>
        Map standard user claims to your Identity Provider's attributes.
        {protocol === "SAML" && " These mappings were auto-detected from your SAML metadata."}
      </Typography>

      {allMapped && (
        <Alert
          severity="success"
          className={classes.successAlert}
          icon={<Assignment />}
        >
          All required claim mappings have been configured successfully.
        </Alert>
      )}

      <Paper className={classes.tableContainer}>
        <Table className={classes.table} size="small">
          <TableHead>
            <TableRow>
              <TableCell><strong>Standard Claim</strong></TableCell>
              <TableCell><strong>Description</strong></TableCell>
              <TableCell><strong>Identity Provider Attribute</strong></TableCell>
              <TableCell><strong>Status</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {standardClaims.map((claim) => (
              <TableRow key={claim.claim}>
                <TableCell>
                  <Typography variant="body2" style={{ fontFamily: 'monospace' }}>
                    {claim.claim}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {claim.description}
                  </Typography>
                </TableCell>
                <TableCell>
                  <TextField
                    fullWidth
                    value={claimMappings[claim.claim] || ""}
                    onChange={(e) => handleClaimUpdate(claim.claim, e.target.value)}
                    placeholder={claim.placeholder}
                    variant="outlined"
                    size="small"
                    required={claim.required}
                  />
                </TableCell>
                <TableCell>
                  {getMappingStatus(claim.claim) ? (
                    <Chip
                      label="Mapped"
                      className={classes.mappedChip}
                      size="small"
                    />
                  ) : (
                    <Chip
                      label="Missing"
                      className={classes.missingChip}
                      size="small"
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      {protocol === "SAML" && (
        <Box style={{ marginTop: 16 }}>
          <Typography variant="caption" color="textSecondary">
            <strong>Note:</strong> For SAML integrations, these are typically URI-based claim types.
            For Azure AD, you may need to use shorter claim names like "emailaddress", "givenname", "surname", "groups".
          </Typography>
        </Box>
      )}

      {protocol === "OIDC" && (
        <Box style={{ marginTop: 16 }}>
          <Typography variant="caption" color="textSecondary">
            <strong>Note:</strong> For OIDC integrations, these are standard claim names as defined in the OpenID Connect specification.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default AccessClaimsConfig;